import { useState } from "react";
import { Trash2, UserPlus, Users } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { MultiSelect } from "@/components/ui/MultiSelect";
import { Spinner } from "@/components/ui/Spinner";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { useAuthStore } from "@/store/auth";

import { useCreateShare, useDeleteShare, useDriveUsers, useGroups, useNodeShares } from "../hooks";
import type { DriveNode, ShareAccess } from "../types";

interface Props {
  node: DriveNode | null;
  onClose: () => void;
}

export function ShareDialog({ node, onClose }: Props) {
  const open = !!node;
  const nodeId = node?.id ?? "";
  const { data: shares = [], isLoading } = useNodeShares(open ? nodeId : null);
  const createShare = useCreateShare(nodeId);
  const deleteShare = useDeleteShare(nodeId);

  const [targetType, setTargetType] = useState<"user" | "group">("user");
  // Multiple targets can be selected and shared at once.
  const [targetIds, setTargetIds] = useState<string[]>([]);
  const [access, setAccess] = useState<ShareAccess>("view");

  // Grupy dostępu są konstrukcją wewnętrzną — `/repository/access-groups/` odmawia
  // kontu podwykonawcy, więc zakładka „Grupa" byłaby u niego zawsze pusta.
  const canUseGroups = useAuthStore((s) => s.user?.role) !== "contractor";

  // Lista z dysku, nie picker Friday: obejmuje podwykonawców wpuszczonych przez CEO
  // (tylko im wolno cokolwiek udostępnić — serwer odrzuca resztę) i działa też wtedy,
  // gdy to podwykonawca udostępnia własny plik do GK.
  const { data: users = [] } = useDriveUsers();
  const { data: groups = [] } = useGroups();

  async function add() {
    if (targetIds.length === 0) return;
    // The backend creates one share per request, so fan out and keep any that
    // failed selected for a retry.
    const results = await Promise.allSettled(
      targetIds.map((id) =>
        createShare.mutateAsync(
          targetType === "user"
            ? { shared_with_user: id, access }
            : { shared_with_group: id, access },
        ),
      ),
    );
    setTargetIds(targetIds.filter((_, i) => results[i].status === "rejected"));
  }

  return (
    <Modal open={open} onClose={onClose} title={`Udostępnij: ${node?.name ?? ""}`} size="md">
      <div className="flex flex-col gap-5">
        {/* Add a new share */}
        <div className="flex flex-col gap-3 border border-[var(--color-border)] p-3">
          {canUseGroups && (
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setTargetType("user");
                  setTargetIds([]);
                }}
                className={`flex-1 px-3 py-1.5 text-xs font-mono uppercase tracking-wider border ${
                  targetType === "user"
                    ? "border-[var(--color-primary)] text-[var(--color-primary)]"
                    : "border-[var(--color-border)] text-[var(--color-text-muted)]"
                }`}
              >
                <UserPlus size={13} className="inline mr-1" /> Osoba
              </button>
              <button
                onClick={() => {
                  setTargetType("group");
                  setTargetIds([]);
                }}
                className={`flex-1 px-3 py-1.5 text-xs font-mono uppercase tracking-wider border ${
                  targetType === "group"
                    ? "border-[var(--color-primary)] text-[var(--color-primary)]"
                    : "border-[var(--color-border)] text-[var(--color-text-muted)]"
                }`}
              >
                <Users size={13} className="inline mr-1" /> Grupa
              </button>
            </div>
          )}

          <MultiSelect
            value={targetIds}
            onChange={setTargetIds}
            placeholder={`— wybierz ${targetType === "user" ? "osoby" : "grupy"} —`}
            searchPlaceholder={targetType === "user" ? "Szukaj osoby..." : "Szukaj grupy..."}
            options={
              targetType === "user"
                ? users.map((u) => ({
                    value: u.id,
                    // Konto spoza GK podpisane wprost — udostępnienie na zewnątrz
                    // ma być świadome, a nie pomyłką w liście nazwisk.
                    label: u.is_contractor ? `${u.full_name} — podwykonawca` : u.full_name,
                  }))
                : groups.map((g) => ({
                    value: g.id,
                    label: `${g.name} (${g.members.length})`,
                  }))
            }
          />

          <div className="flex items-center gap-2">
            <select
              className="input-base flex-1"
              value={access}
              onChange={(e) => setAccess(e.target.value as ShareAccess)}
            >
              <option value="view">Odczyt</option>
              <option value="edit">Edycja</option>
            </select>
            <Button
              onClick={add}
              disabled={targetIds.length === 0}
              loading={createShare.isPending}
            >
              {targetIds.length > 1 ? `Udostępnij (${targetIds.length})` : "Udostępnij"}
            </Button>
          </div>
          {targetType === "group" && groups.length === 0 && (
            <p className="text-xs font-mono text-[var(--color-text-muted)]">
              Brak grup. Utwórz grupę przyciskiem „Grupy dostępu".
            </p>
          )}
        </div>

        {/* Existing shares */}
        <div className="flex flex-col gap-2">
          <span className="text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)]">
            Udostępniono
          </span>
          {isLoading ? (
            <Spinner size="sm" />
          ) : shares.length === 0 ? (
            <p className="text-sm text-[var(--color-text-muted)]">Jeszcze nikomu nie udostępniono.</p>
          ) : (
            <ul className="flex flex-col gap-1">
              {shares.map((s) => (
                <li
                  key={s.id}
                  className="flex items-center gap-2 px-2 py-1.5 border border-[var(--color-border)]"
                >
                  {s.target.type === "user" ? (
                    <UserAvatar fullName={s.target.name} profilePictureUrl={null} size="xs" />
                  ) : (
                    <Users size={14} className="text-[var(--color-text-muted)]" />
                  )}
                  <span className="text-sm flex-1 truncate">{s.target.name}</span>
                  <span className="text-[10px] font-mono uppercase text-[var(--color-text-muted)]">
                    {s.access === "edit" ? "Edycja" : "Odczyt"}
                  </span>
                  <button
                    onClick={() => deleteShare.mutate(s.id)}
                    className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                    title="Odwołaj"
                  >
                    <Trash2 size={14} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Modal>
  );
}
